/**
 * Dialog Handler Utilities
 *
 * This file provides utility functions for handling dialogs in an Office Add-in
 */
import { dialogService, DialogEventType } from "./dialog";
// Lưu trữ dialog đang mở
let currentDialogInstance = null;
// Token tracking cho dialog requests
let lastDialogToken = "";
// BroadcastChannel management
let dialogBroadcastChannel = null;
let componentBroadcastChannel = null;
const DIALOG_CHANNEL_NAME = "dialog-communication";
/**
 * Cache cho các URL dialog đã mở
 */
const dialogCache = new Map();
/**
 * Khởi tạo BroadcastChannel cho component (parent)
 * Gọi function này trong component muốn gửi data lên dialog
 */
export function initializeComponentBroadcast(messageHandler) {
    // Đóng channel cũ nếu có
    if (componentBroadcastChannel) {
        componentBroadcastChannel.close();
    }
    // Tạo channel mới
    componentBroadcastChannel = new BroadcastChannel(DIALOG_CHANNEL_NAME);
    // Set up message handler
    if (messageHandler) {
        componentBroadcastChannel.onmessage = (event) => {
            try {
                const data = event.data;
                console.log("Component received broadcast:", data);
                messageHandler(data);
            }
            catch (error) {
                console.error("Error handling broadcast message in component:", error);
            }
        };
    }
    console.log("Component BroadcastChannel initialized");
    return componentBroadcastChannel;
}
/**
 * Khởi tạo BroadcastChannel cho dialog
 * Gọi function này trong dialog để nhận data từ components
 */
export function initializeDialogBroadcast(messageHandler) {
    // Đóng channel cũ nếu có
    if (dialogBroadcastChannel) {
        dialogBroadcastChannel.close();
    }
    // Tạo channel mới
    dialogBroadcastChannel = new BroadcastChannel(DIALOG_CHANNEL_NAME);
    // Set up message handler
    if (messageHandler) {
        dialogBroadcastChannel.onmessage = (event) => {
            try {
                const data = event.data;
                console.log("Dialog received broadcast:", data);
                messageHandler(data);
            }
            catch (error) {
                console.error("Error handling broadcast message in dialog:", error);
            }
        };
    }
    console.log("Dialog BroadcastChannel initialized");
    return dialogBroadcastChannel;
}
/**
 * Gửi data từ component lên dialog
 */
export function sendDataToDialog(type, payload, dialogId) {
    if (!componentBroadcastChannel) {
        console.warn("Component BroadcastChannel not initialized. Call initializeComponentBroadcast() first.");
        return false;
    }
    try {
        const data = {
            type,
            payload,
            timestamp: Date.now(),
            dialogId,
            componentId: `component_${Date.now()}`,
        };
        componentBroadcastChannel.postMessage(data);
        console.log("Data sent to dialog:", data);
        return true;
    }
    catch (error) {
        console.error("Error sending data to dialog:", error);
        return false;
    }
}
/**
 * Gửi data từ dialog về component
 */
export function sendDataToComponent(type, payload, componentId) {
    if (!dialogBroadcastChannel) {
        console.warn("Dialog BroadcastChannel not initialized. Call initializeDialogBroadcast() first.");
        return false;
    }
    try {
        const data = {
            type,
            payload,
            timestamp: Date.now(),
            componentId,
            dialogId: `dialog_${Date.now()}`,
        };
        dialogBroadcastChannel.postMessage(data);
        console.log("Data sent to component:", data);
        return true;
    }
    catch (error) {
        console.error("Error sending data to component:", error);
        return false;
    }
}
/**
 * Đóng BroadcastChannels
 */
export function closeBroadcastChannels() {
    if (componentBroadcastChannel) {
        componentBroadcastChannel.close();
        componentBroadcastChannel = null;
        console.log("Component BroadcastChannel closed");
    }
    if (dialogBroadcastChannel) {
        dialogBroadcastChannel.close();
        dialogBroadcastChannel = null;
        console.log("Dialog BroadcastChannel closed");
    }
}
/**
 * Utility functions cho các message types thường dùng
 */
export const DialogBroadcastTypes = {
    // Component -> Dialog
    SEND_FORM_DATA: "SEND_FORM_DATA",
    SEND_USER_SELECTION: "SEND_USER_SELECTION",
    SEND_CONFIG_DATA: "SEND_CONFIG_DATA",
    UPDATE_DIALOG_STATE: "UPDATE_DIALOG_STATE",
    // Dialog -> Component
    DIALOG_FORM_SUBMIT: "DIALOG_FORM_SUBMIT",
    DIALOG_DATA_CHANGED: "DIALOG_DATA_CHANGED",
    DIALOG_ACTION_COMPLETED: "DIALOG_ACTION_COMPLETED",
    REQUEST_COMPONENT_DATA: "REQUEST_COMPONENT_DATA",
};
/**
 * Helper functions cho các use cases thường gặp
 */
export const DialogBroadcastHelpers = {
    /**
     * Gửi form data từ component lên dialog
     */
    sendFormData: (formData, dialogId) => {
        return sendDataToDialog(DialogBroadcastTypes.SEND_FORM_DATA, formData, dialogId);
    },
    /**
     * Gửi user selection từ component lên dialog
     */
    sendUserSelection: (selection, dialogId) => {
        return sendDataToDialog(DialogBroadcastTypes.SEND_USER_SELECTION, selection, dialogId);
    },
    /**
     * Gửi config data từ component lên dialog
     */
    sendConfigData: (config, dialogId) => {
        return sendDataToDialog(DialogBroadcastTypes.SEND_CONFIG_DATA, config, dialogId);
    },
    /**
     * Dialog gửi form submit result về component
     */
    sendFormSubmitResult: (result, componentId) => {
        return sendDataToComponent(DialogBroadcastTypes.DIALOG_FORM_SUBMIT, result, componentId);
    },
    /**
     * Dialog request data từ component
     */
    requestComponentData: (dataType, componentId) => {
        return sendDataToComponent(DialogBroadcastTypes.REQUEST_COMPONENT_DATA, { dataType }, componentId);
    },
};
/**
 * Function to open a dialog with custom callback handler
 *
 * @param url URL to open in the dialog
 * @param options Dialog options và callback options
 * @returns Promise that resolves with the dialog model
 */
export function openDialogWithCallback(url, options) {
    // Nếu dialog đang mở, đóng nó trước
    if (currentDialogInstance) {
        try {
            currentDialogInstance.close();
        }
        catch (e) {
            console.warn("Error closing previous dialog:", e);
        }
        currentDialogInstance = null;
    }
    // Tạo token mới cho dialog request này
    lastDialogToken = `dialog_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const currentToken = lastDialogToken;
    // Ensure URL has the origin
    if (!url.startsWith("http")) {
        const baseUrl = window.location.origin;
        url = `${baseUrl}/${url.replace(/^\//, "")}`;
    }
    // Add timestamp to prevent caching
    if (!url.includes("t=")) {
        const separator = url.includes("?") ? "&" : "?";
        url = `${url}${separator}t=${Date.now()}`;
    }
    // Lưu URL vào cache
    const urlWithoutTimestamp = url.replace(/[?&]t=\d+/, "");
    dialogCache.set(urlWithoutTimestamp, {
        url: url,
        lastOpened: Date.now(),
    });
    console.log(`Opening dialog with URL: ${url}`);
    return new Promise((resolve, reject) => {
        // Timeout handling
        let timeoutId = window.setTimeout(() => {
            if (currentToken === lastDialogToken) {
                console.warn("Dialog open operation timed out after 10 seconds");
                reject(new Error("Dialog open timed out after 10 seconds"));
            }
        }, 10000);
        dialogService.ShowDialog(url, {
            Width: options.width || 80,
            Height: options.height || 60,
            DisplayInIframe: true,
        }, (dialog) => {
            // Hủy timeout khi dialog đã mở thành công
            clearTimeout(timeoutId);
            // Kiểm tra xem dialog request này có còn là request mới nhất không
            if (currentToken !== lastDialogToken) {
                console.log("Dialog request superseded by newer request");
                try {
                    dialog.close();
                }
                catch (e) {
                    // Ignore errors on cleanup
                }
                return;
            }
            // Lưu reference đến dialog hiện tại
            currentDialogInstance = dialog;
            // Xử lý event received
            dialog.addEventHandler(DialogEventType.DialogEventReceived, (args) => {
                // Nếu có custom handler và nó xử lý event này
                if (options.callbackOptions.onEventReceived && options.callbackOptions.onEventReceived(args, dialog)) {
                    return; // Custom handler đã xử lý
                }
                // Xử lý mặc định
                if ("error" in args) {
                    if (args.error === 12006) {
                        // Dialog closed
                        console.log("Dialog closed by user");
                        currentDialogInstance = null;
                        // Đóng BroadcastChannels khi dialog đóng
                        closeBroadcastChannels();
                        // Callback khi đóng dialog
                        if (options.callbackOptions.onDialogClosed) {
                            options.callbackOptions.onDialogClosed();
                        }
                    }
                }
            });
            // Xử lý message received
            dialog.addEventHandler(DialogEventType.DialogMessageReceived, (args) => {
                // Nếu có custom handler và nó xử lý message này
                if (options.callbackOptions.onMessageReceived && options.callbackOptions.onMessageReceived(args, dialog)) {
                    return; // Custom handler đã xử lý
                }
                // Xử lý mặc định nếu không có custom handler
                if (args.message) {
                    try {
                        const message = JSON.parse(args.message);
                        console.log("Received message from dialog:", message);
                        // Xử lý message type mặc định
                        if (message.type === "DIALOG_READY") {
                            console.log("Dialog is ready");
                        }
                        else if (message.type === "DIALOG_CLOSE") {
                            dialog.close();
                            currentDialogInstance = null;
                        }
                    }
                    catch (e) {
                        console.error("Error parsing dialog message:", e);
                    }
                }
            });
            // Khởi tạo BroadcastChannel cho dialog nếu có callback
            if (options.callbackOptions.onBroadcastReceived) {
                initializeDialogBroadcast(options.callbackOptions.onBroadcastReceived);
            }
            // Gọi custom dialog open handler
            if (options.callbackOptions.onDialogOpen) {
                options.callbackOptions.onDialogOpen(dialog);
            }
            // Resolve promise với dialog instance
            resolve(dialog);
        });
    });
}
/**
 * Function to open a dialog and set up basic event handlers
 *
 * @param url URL to open in the dialog
 * @param width Dialog width (1-100)
 * @param height Dialog height (1-100)
 * @param messageHandler Function to handle messages from the dialog
 * @param errorHandler Function to handle dialog errors
 * @returns Promise that resolves with the dialog model
 */
export function openDialog(url, width = 80, height = 60, messageHandler, errorHandler) {
    // Wrap the handlers inside DialogCallbackOptions
    const callbackOptions = {
        onMessageReceived: messageHandler
            ? (args, dialog) => {
                if (args.message) {
                    try {
                        // Try to parse the message as JSON
                        const message = JSON.parse(args.message);
                        messageHandler(message);
                    }
                    catch (e) {
                        // If not JSON, pass the raw message
                        messageHandler(args.message);
                    }
                }
                return true; // Indicate that we handled the message
            }
            : undefined,
        onEventReceived: errorHandler
            ? (args, dialog) => {
                if ("error" in args) {
                    if (args.error !== 12006) {
                        // 12006 is dialog closed
                        errorHandler(args.error);
                    }
                }
                return false; // Continue with default handling
            }
            : undefined,
    };
    // Use the new function with the wrapped options
    return openDialogWithCallback(url, {
        width,
        height,
        callbackOptions,
    });
}
/**
 * Function to close an open dialog
 */
export function closeDialog() {
    // Đóng BroadcastChannels trước khi đóng dialog
    closeBroadcastChannels();
    if (currentDialogInstance) {
        try {
            currentDialogInstance.close();
        }
        catch (e) {
            console.warn("Error closing dialog:", e);
        }
        currentDialogInstance = null;
    }
    else {
        dialogService.CloseDialog();
    }
}
/**
 * Mở lại dialog đã mở gần đây nhất
 */
export function reopenLastDialog(callbackOptions) {
    if (dialogCache.size === 0) {
        console.warn("No previous dialogs to reopen");
        return null;
    }
    // Tìm URL dialog đã mở gần đây nhất
    let latestUrl = "";
    let latestTime = 0;
    dialogCache.forEach((entry) => {
        if (entry.lastOpened > latestTime) {
            latestTime = entry.lastOpened;
            latestUrl = entry.url;
        }
    });
    if (latestUrl) {
        return openDialogWithCallback(latestUrl, {
            width: 80,
            height: 60,
            callbackOptions,
        });
    }
    return null;
}
/**
 * Initialize as a dialog page
 * This should be called in a dialog page to set up message handlers
 *
 * @param messageHandler Function to handle messages from the parent
 */
export function initializeAsDialog(messageHandler) {
    if (!messageHandler)
        return;
    // Sử dụng biến để theo dõi trạng thái khởi tạo
    let initialized = false;
    // Set up message listener if we're in a dialog
    if (typeof Office !== "undefined" && Office.context) {
        window.addEventListener("message", (event) => {
            if (event.data) {
                messageHandler(event.data);
            }
        });
        // Gửi ready message sau khi Office đã sẵn sàng
        const readyHandler = () => {
            if (initialized)
                return;
            initialized = true;
            try {
                // Try to send message to parent
                if (Office.context.ui) {
                    // Using setTimeout to ensure UI is fully loaded
                    setTimeout(() => {
                        try {
                            Office.context.ui.messageParent(JSON.stringify({
                                type: "DIALOG_READY",
                                timestamp: Date.now(),
                            }));
                            console.log("Sent DIALOG_READY message to parent");
                        }
                        catch (e) {
                            console.error("Error sending ready message to parent:", e);
                        }
                    }, 300);
                }
            }
            catch (e) {
                console.error("Error initializing dialog:", e);
            }
        };
        // Office.onReady với timeout backup
        if (Office.context.requirements && Office.context.requirements.isSetSupported("DialogApi", "1.1")) {
            Office.onReady(readyHandler);
        }
        else {
            // Fallback nếu Office.onReady không hoạt động
            setTimeout(readyHandler, 1000);
        }
    }
}
